import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  ExpandedState,
  SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { getPeriodStatusBadge } from 'utils/helpers/constVariables/periodStatuses';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import ChevronDown from 'assets/icons/chevron-down';

export const useFacilityPeriodTable = (data: any[]) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>(() => {
    const initialState: Record<string, boolean> = {};
    data.forEach((_, index) => {
      initialState[index] = true;
    });
    return initialState;
  });

  const columns: ColumnDef<any>[] = useMemo(() => {
    return [
      {
        id: 'executor',
        header: 'Исполнитель',
        size: 200,
        enableSorting: true,
        cell: ({ row: { original, depth, id } }) => {
          if (!original) return '-';

          let displayName = '';
          let link = '';
          let count = 0;

          if (depth === 0) {
            displayName = original.name;
            count = original.users?.length || 0;
          } else if (depth === 1) {
            displayName = original.name;
            link = `/users/${original.id}`;
            count = original.positions?.length || 0;
          } else if (depth === 2) {
            return null;
          }

          return (
            <div className="flex items-center gap-2">
              {depth === 1 && count > 1 && (
                <ChevronDown
                  className={clsx(
                    'transition-transform duration-200 link',
                    (expanded as Record<string, boolean>)[id] ? 'rotate-180' : '',
                  )}
                />
              )}
              {link ? (
                <Link to={link} className="link">
                  {displayName}
                  {count > 0 ? ` (${count})` : ''}
                </Link>
              ) : (
                <span>
                  {displayName}
                  {count > 0 ? ` (${count})` : ''}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'profession',
        header: 'Профессия',
        size: 200,
        enableSorting: true,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 2) {
            return (
              <div className="flex items-center gap-2">
                <span>{original.name}</span>
              </div>
            );
          }
          if (!original.positions) return '-';
          return original.positions.map((pos: { name: string }) => pos.name).join(', ');
        },
      },
      {
        id: 'shifts',
        header: 'Смены',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.totalShifts || 0), 0);
            return <div className="text-right">{total}</div>;
          }
          return <div className="text-right">{original.totalShifts || 0}</div>;
        },
      },
      {
        id: 'fact',
        header: 'Факт',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const totalHours = original.positions.reduce((sum: number, pos: any) => {
              const hours = parseInt(pos.fact) || 0;
              return sum + hours;
            }, 0);
            return <div className="text-right">{`${totalHours} ч.`}</div>;
          }
          return <div className="text-right">{original.fact || '-'}</div>;
        },
      },
      {
        id: 'rate',
        header: 'По ставке',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.rate || 0), 0);
            return <div className="text-right">{formatMoney(total)}</div>;
          }
          return <div className="text-right">{formatMoney(original.rate || 0)}</div>;
        },
      },
      {
        id: 'bonus',
        header: 'Бонусы',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.bonus || 0), 0);
            return <div className="text-right">{formatMoney(total)}</div>;
          }
          return <div className="text-right">{formatMoney(original.bonus || 0)}</div>;
        },
      },
      {
        id: 'hold',
        header: 'Удержания',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.retention || 0), 0);
            return <div className="text-right">{formatMoney(total)}</div>;
          }
          return <div className="text-right">{formatMoney(original.retention || 0)}</div>;
        },
      },
      {
        id: 'surcharge',
        header: 'Надбавки',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.allowance || 0), 0);
            return <div className="text-right">{formatMoney(total)}</div>;
          }
          return <div className="text-right">{formatMoney(original.allowance || 0)}</div>;
        },
      },
      {
        id: 'sum',
        header: 'Сумма',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.sum || 0), 0);
            return <div className="text-right">{formatMoney(total)}</div>;
          }
          return <div className="text-right">{formatMoney(original.sum || 0)}</div>;
        },
      },
      {
        id: 'sumWithNpd',
        header: 'Сумма с НПД',
        size: 200,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const total = original.positions.reduce((sum: number, pos: any) => sum + (pos.sumWithNpd || 0), 0);
            return <div className="text-right">{formatMoney(total)}</div>;
          }
          return <div className="text-right">{formatMoney(original.sumWithNpd || 0)}</div>;
        },
      },
      {
        id: 'status',
        header: 'Статус',
        size: 200,
        enableSorting: true,
        cell: ({ row: { original, depth } }) => {
          if (depth === 0) return null;
          if (depth === 1) {
            const hasRejected = original.positions.some((pos: any) => pos.status === 'rejected');
            const status = hasRejected ? 'rejected' : 'active';
            const statusBadge = getPeriodStatusBadge(status);
            return (
              <span className={clsx('px-2 py-1 rounded-full text-xs', statusBadge.className)}>{statusBadge.text}</span>
            );
          }
          const statusBadge = getPeriodStatusBadge(original.status || 'Неизвестно');
          return (
            <span className={clsx('px-2 py-1 rounded-full text-xs', statusBadge.className)}>{statusBadge.text}</span>
          );
        },
      },
    ];
  }, [expanded]);

  const [columnOrder, setColumnOrder] = useState(columns.map(column => column.id as string));

  const getSubRows = (row: any) => {
    if (row.users) {
      // Если есть users - это объект, возвращаем пользователей
      return row.users;
    } else if (row.positions && !row.isPosition) {
      // Если есть positions и это не позиция - это пользователь, возвращаем позиции
      return row.positions;
    }
    return undefined;
  };

  return useReactTable({
    data,
    state: {
      columnOrder,
      sorting,
      expanded,
    },
    getSubRows,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onExpandedChange: setExpanded,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    columns,
  });
};

export const formatMoney = (value: number) => {
  return Math.round(value * 100) / 100;
};
